import { useId, useMemo, useRef, useState } from 'react';
import type { KeyboardEvent } from 'react';
import type { BrandOption } from '@/services/api';
import { fuzzyFilterSort } from '@/utils/fuzzyMatch';
import { useBrandPreferences } from '@/hooks/useBrandPreferences';

interface Props {
  id: string;
  label: string;
  brands: BrandOption[];
  loading: boolean;
  value: string; // slug
  onChange: (slug: string) => void;
  excludeSlug?: string;
  disabled?: boolean;
}

type DisplayRow = { type: 'header'; key: string; label: string } | { type: 'option'; key: string; brand: BrandOption };

const VERIFICATION_LABEL: Record<BrandOption['verificationStatus'], string> = {
  verified: 'Verified',
  pending: 'Pending verification',
  rejected: 'Rejected',
};

const VERIFICATION_CLASS: Record<BrandOption['verificationStatus'], string> = {
  verified: 'bg-brand/10 text-brand',
  pending: 'bg-stone-100 text-muted',
  rejected: 'bg-danger/10 text-danger',
};

/**
 * Searchable, fuzzy-matching brand combobox — replaces the plain <select>.
 * Same prop contract as the old BrandSelect so it drops into both pages
 * without page-level changes beyond the import.
 */
export function BrandSearch({ id, label, brands, loading, value, onChange, excludeSlug, disabled }: Props) {
  const visible = useMemo(() => (excludeSlug ? brands.filter((b) => b.slug !== excludeSlug) : brands), [brands, excludeSlug]);
  const selected = visible.find((b) => b.slug === value) ?? null;

  const { recent, favorites, markRecent, toggleFavorite } = useBrandPreferences();
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listboxId = useId();

  const rows: DisplayRow[] = useMemo(() => {
    const byslug = new Map(visible.map((b) => [b.slug, b]));
    const trimmed = query.trim();

    if (trimmed.length > 0) {
      const matches = fuzzyFilterSort(trimmed, visible, (b) => b.name);
      return matches.map((b) => ({ type: 'option', key: b.slug, brand: b }));
    }

    const out: DisplayRow[] = [];
    const favoriteBrands = favorites.map((s) => byslug.get(s)).filter((b): b is BrandOption => !!b);
    const recentBrands = recent
      .map((s) => byslug.get(s))
      .filter((b): b is BrandOption => !!b && !favorites.includes(b.slug));

    if (favoriteBrands.length > 0) {
      out.push({ type: 'header', key: 'h-fav', label: 'Favourites' });
      favoriteBrands.forEach((b) => out.push({ type: 'option', key: `fav-${b.slug}`, brand: b }));
    }
    if (recentBrands.length > 0) {
      out.push({ type: 'header', key: 'h-recent', label: 'Recent' });
      recentBrands.forEach((b) => out.push({ type: 'option', key: `recent-${b.slug}`, brand: b }));
    }
    // Everything already shown above (favourites/recent) is excluded here —
    // each brand appears exactly once in the list, never duplicated.
    const alreadyShown = new Set([...favoriteBrands, ...recentBrands].map((b) => b.slug));
    out.push({ type: 'header', key: 'h-all', label: 'All brands' });
    [...visible]
      .filter((b) => !alreadyShown.has(b.slug))
      .sort((a, b) => a.name.localeCompare(b.name))
      .forEach((b) => out.push({ type: 'option', key: `all-${b.slug}`, brand: b }));
    return out;
  }, [query, visible, favorites, recent]);

  const optionRows = rows.filter((r): r is Extract<DisplayRow, { type: 'option' }> => r.type === 'option');

  function open() {
    if (disabled) return;
    setIsOpen(true);
    setHighlightedIndex(0);
  }

  function selectBrand(brand: BrandOption) {
    if (!brand.isAvailable) return;
    onChange(brand.slug);
    markRecent(brand.slug);
    setQuery('');
    setIsOpen(false);
  }

  function handleKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (!isOpen && (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Enter')) {
      open();
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex((i) => Math.min(optionRows.length - 1, i + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex((i) => Math.max(0, i - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const opt = optionRows[highlightedIndex];
      if (opt) selectBrand(opt.brand);
    } else if (e.key === 'Escape') {
      setIsOpen(false);
      setQuery('');
      inputRef.current?.blur();
    }
  }

  const highlightedOptionKey = optionRows[highlightedIndex]?.key;
  const displayValue = isOpen ? query : (selected?.name ?? '');

  return (
    <div className="relative">
      <label htmlFor={id} className="mb-1 block text-sm font-medium text-ink">
        {label}
      </label>
      <div className="relative">
        <input
          ref={inputRef}
          id={id}
          type="text"
          role="combobox"
          aria-expanded={isOpen}
          aria-controls={listboxId}
          aria-autocomplete="list"
          aria-activedescendant={isOpen && highlightedOptionKey ? `${listboxId}-${highlightedOptionKey}` : undefined}
          autoComplete="off"
          disabled={disabled}
          placeholder={loading ? 'Loading brands…' : 'Search brands…'}
          value={displayValue}
          onFocus={(e) => {
            open();
            e.currentTarget.select();
          }}
          onChange={(e) => {
            setQuery(e.target.value);
            setIsOpen(true);
            setHighlightedIndex(0);
          }}
          onKeyDown={handleKeyDown}
          onBlur={() => setIsOpen(false)}
          className="min-h-[46px] w-full rounded-xl border border-stone-200 bg-white px-3 py-2 text-base text-ink shadow-sm disabled:opacity-50"
        />
        {isOpen && (
          <ul
            id={listboxId}
            role="listbox"
            aria-label={`${label} suggestions`}
            className="absolute z-10 mt-1 max-h-72 w-full overflow-y-auto rounded-xl border border-stone-200 bg-white py-1 shadow-card"
          >
            {loading && <li className="px-3 py-2 text-sm text-muted">Loading brands…</li>}
            {!loading && rows.length === 0 && <li className="px-3 py-2 text-sm text-muted">No brands match "{query}".</li>}
            {!loading &&
              rows.map((row) => {
                if (row.type === 'header') {
                  return (
                    <li key={row.key} className="px-3 pb-1 pt-2 text-xs font-semibold uppercase tracking-wide text-muted">
                      {row.label}
                    </li>
                  );
                }
                const b = row.brand;
                const isHighlighted = row.key === highlightedOptionKey;
                const isFavorite = favorites.includes(b.slug);
                return (
                  <li
                    key={row.key}
                    id={`${listboxId}-${row.key}`}
                    role="option"
                    aria-selected={isHighlighted}
                    aria-disabled={!b.isAvailable}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => selectBrand(b)}
                    className={`flex cursor-pointer items-center gap-2 px-3 py-2 text-sm ${
                      !b.isAvailable ? 'cursor-not-allowed opacity-50' : isHighlighted ? 'bg-brand/10' : 'hover:bg-stone-50'
                    }`}
                  >
                    <button
                      type="button"
                      aria-label={isFavorite ? `Remove ${b.name} from favourites` : `Add ${b.name} to favourites`}
                      aria-pressed={isFavorite}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleFavorite(b.slug);
                      }}
                      className={`shrink-0 text-base leading-none ${isFavorite ? 'text-brand' : 'text-stone-300 hover:text-stone-400'}`}
                    >
                      {isFavorite ? '★' : '☆'}
                    </button>

                    {b.logoUrl ? (
                      <img src={b.logoUrl} alt="" aria-hidden="true" className="h-5 w-5 shrink-0 rounded object-contain" />
                    ) : (
                      <span
                        aria-hidden="true"
                        className="flex h-5 w-5 shrink-0 items-center justify-center rounded bg-stone-100 text-[10px] font-semibold text-muted"
                      >
                        {b.name.charAt(0).toUpperCase()}
                      </span>
                    )}

                    <span className="flex-1 truncate text-ink">{b.name}</span>

                    <span className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${VERIFICATION_CLASS[b.verificationStatus]}`}>
                      {VERIFICATION_LABEL[b.verificationStatus]}
                    </span>
                  </li>
                );
              })}
          </ul>
        )}
      </div>
      {selected && !selected.isAvailable && selected.reasonUnavailable && (
        <p className="mt-2 text-sm text-muted" role="status">
          {selected.reasonUnavailable}
        </p>
      )}
    </div>
  );
}
